import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faBookOpen, faCalendarCheck, faChevronLeft, faClock, faQuestionCircle, faSearch, faShieldHeart, faTooth } from '@fortawesome/free-solid-svg-icons';
import TituloPagina from '../../js/Components/PageTitle';
import '../../css/DentalEducation.css';
import { branches, getWhatsappUrl } from '../data/branches';
import { dentalEducationArticles, educationCategories, featuredEducationSlugs, getEducationArticleBySlug, type DentalEducationArticle, type EducationCategory } from '../data/dentalEducation';

const educationBasePath = '/InformacionDental';
const giganteraBranch = branches.find((branch) => branch.id === 'gigantera') || branches[0];

const upsertMeta = (name: string, content: string) => {
  if (typeof document === 'undefined') return;
  let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
};

const EducationCta: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <section className={`education-cta ${compact ? 'education-cta-compact' : ''}`}>
    <div>
      <span className="education-kicker">¿Tienes molestias o dudas?</span>
      <h2>Agenda una valoración en DentalPuente</h2>
      <p>La información de esta biblioteca es educativa. Para saber qué tratamiento necesitas, revisamos tu caso, síntomas, historial y radiografías cuando aplique.</p>
    </div>
    <div className="education-cta-actions">
      <a className="education-primary-link" href={getWhatsappUrl(giganteraBranch, 'Hola DentalPuente, quiero agendar una valoración dental.')} target="_blank" rel="noreferrer">
        Agendar por WhatsApp
      </a>
      <Link className="education-secondary-link" to="/Contact">Ver sucursales</Link>
    </div>
  </section>
);

const ArticleCard: React.FC<{ article: DentalEducationArticle; featured?: boolean }> = ({ article, featured = false }) => {
  const category = educationCategories[article.category];
  return (
    <article className={`education-card ${featured ? 'education-card-featured' : ''}`}>
      <div className="education-card-topline">
        <span>{category.label}</span>
        <small><FontAwesomeIcon icon={faClock} /> {article.readingTime}</small>
      </div>
      <h3>{article.title}</h3>
      <p>{article.description}</p>
      <Link to={`${educationBasePath}/${article.slug}`} className="education-card-link">
        Leer guía <FontAwesomeIcon icon={faArrowRight} />
      </Link>
    </article>
  );
};

const EducationLanding: React.FC = () => {
  React.useEffect(() => {
    document.title = 'Información Dental | Endodoncia, Ortodoncia y Prevención | DentalPuente';
    upsertMeta('description', 'Biblioteca dental de DentalPuente con guías claras sobre endodoncia, ortodoncia, brackets, caries, prevención y urgencias dentales para pacientes en México.');
    upsertMeta('keywords', 'información dental, endodoncia, ortodoncia, brackets, caries, dentista guadalajara, dentalpuente');
  }, []);

  const [activeCategory, setActiveCategory] = React.useState<EducationCategory | 'todos'>('todos');
  const filteredArticles = activeCategory === 'todos' ? dentalEducationArticles : dentalEducationArticles.filter((article) => article.category === activeCategory);
  const featuredArticles = featuredEducationSlugs.map((slug) => getEducationArticleBySlug(slug)).filter(Boolean) as DentalEducationArticle[];

  return (
    <div className="content-wrap education-page">
      <TituloPagina title="Información dental" />

      <section className="education-hero">
        <div className="education-hero-copy">
          <span className="education-kicker"><FontAwesomeIcon icon={faBookOpen} /> Biblioteca DentalPuente</span>
          <h1>Aprende términos dentales antes de llegar a consulta</h1>
          <p className="education-hero-lead">Creamos una sección informativa para explicar endodoncia, ortodoncia, prevención, dolor dental y conceptos básicos con lenguaje claro, enfoque clínico y utilidad real para pacientes.</p>
          <div className="education-hero-actions">
            <a href="#guias" className="education-primary-link">Explorar guías</a>
            <Link to="/Contact" className="education-secondary-link">Agendar valoración</Link>
          </div>
        </div>
        <aside className="education-hero-panel" aria-label="Resumen de la biblioteca dental">
          <div className="education-hero-stat"><strong>{dentalEducationArticles.length}</strong><span>guías iniciales</span></div>
          <div className="education-hero-stat"><strong>4</strong><span>categorías útiles</span></div>
          <div className="education-hero-note"><FontAwesomeIcon icon={faShieldHeart} /> Información educativa; no sustituye diagnóstico clínico.</div>
        </aside>
      </section>

      <section className="education-feature-strip" aria-label="Categorías principales">
        {Object.entries(educationCategories).map(([key, category]) => (
          <button key={key} type="button" className={`education-category-pill ${activeCategory === key ? 'active' : ''}`} onClick={() => setActiveCategory(key as EducationCategory)}>
            <span>{category.shortLabel}</span>
            <small>{category.description}</small>
          </button>
        ))}
        <button type="button" className={`education-category-pill ${activeCategory === 'todos' ? 'active' : ''}`} onClick={() => setActiveCategory('todos')}>
          <span>Todas</span>
          <small>Ver biblioteca completa.</small>
        </button>
      </section>

      <section className="education-section" id="destacadas">
        <div className="education-section-heading">
          <span className="education-kicker">Inicio recomendado</span>
          <h2>Guías destacadas para pacientes</h2>
          <p>Contenido pensado para resolver dudas frecuentes antes de una valoración dental, sin tecnicismos innecesarios.</p>
        </div>
        <div className="education-grid education-grid-featured">
          {featuredArticles.map((article) => <ArticleCard key={article.slug} article={article} featured />)}
        </div>
      </section>

      <section className="education-section" id="guias">
        <div className="education-section-heading education-section-heading-inline">
          <div>
            <span className="education-kicker"><FontAwesomeIcon icon={faSearch} /> Biblioteca completa</span>
            <h2>{activeCategory === 'todos' ? 'Todas las guías dentales' : educationCategories[activeCategory].label}</h2>
          </div>
          <p>{filteredArticles.length} artículos disponibles.</p>
        </div>
        <div className="education-grid">
          {filteredArticles.map((article) => <ArticleCard key={article.slug} article={article} />)}
        </div>
      </section>

      <section className="education-terminology-preview">
        <div>
          <span className="education-kicker"><FontAwesomeIcon icon={faTooth} /> Terminología básica</span>
          <h2>Diccionario rápido: palabras que escucharás en consulta</h2>
          <p>Más de 70 términos de endodoncia, ortodoncia, encías, prevención, cirugía oral y restauraciones explicados de forma sencilla.</p>
        </div>
        <Link to={`${educationBasePath}/diccionario-dental-basico`} className="education-primary-link">Abrir diccionario</Link>
      </section>

      <EducationCta />
    </div>
  );
};

const EducationArticlePage: React.FC<{ article: DentalEducationArticle }> = ({ article }) => {
  React.useEffect(() => {
    document.title = `${article.title} | DentalPuente`;
    upsertMeta('description', article.description);
    upsertMeta('keywords', `${article.title}, ${educationCategories[article.category].label}, DentalPuente, dentista Guadalajara, México`);
  }, [article]);

  const relatedArticles = article.relatedSlugs.map((slug) => getEducationArticleBySlug(slug)).filter(Boolean) as DentalEducationArticle[];

  return (
    <div className="content-wrap education-page education-article-page">
      <Link to={educationBasePath} className="education-back-link"><FontAwesomeIcon icon={faChevronLeft} /> Volver a Información Dental</Link>

      <article className="education-article-shell">
        <header className="education-article-hero">
          <span className="education-kicker">{article.eyebrow}</span>
          <h1>{article.title}</h1>
          <p>{article.heroSummary}</p>
          <div className="education-article-meta">
            <span><FontAwesomeIcon icon={faClock} /> Lectura: {article.readingTime}</span>
            <span><FontAwesomeIcon icon={faBookOpen} /> {educationCategories[article.category].label}</span>
          </div>
        </header>

        <section className="education-takeaways" aria-label="Puntos clave">
          <h2>Puntos clave</h2>
          <ul>
            {article.keyTakeaways.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </section>

        <div className="education-article-layout">
          <div className="education-article-content">
            {article.sections.map((section) => (
              <section key={section.heading} className="education-content-section">
                <h2>{section.heading}</h2>
                {section.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </section>
            ))}

            <section className="education-glossary-box">
              <h2>Glosario útil</h2>
              <div className="education-glossary-grid">
                {article.glossary.map((item) => (
                  <div key={item.term} className="education-glossary-item">
                    <strong>{item.term}</strong>
                    <p>{item.definition}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="education-faq-box">
              <h2><FontAwesomeIcon icon={faQuestionCircle} /> Preguntas frecuentes</h2>
              {article.faqs.map((faq) => (
                <details key={faq.question}>
                  <summary>{faq.question}</summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </section>

            <section className="education-sources-box">
              <h2>Fuentes consultadas</h2>
              <p>Contenido redactado para pacientes con base en fuentes odontológicas y normativas de referencia. No reemplaza consulta, diagnóstico ni plan de tratamiento individual.</p>
              <ul>
                {article.sources.map((source) => (
                  <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.label}</a></li>
                ))}
              </ul>
            </section>
          </div>

          <aside className="education-article-aside">
            <div className="education-aside-card">
              <span className="education-kicker"><FontAwesomeIcon icon={faCalendarCheck} /> Siguiente paso</span>
              <h3>Confirma tu diagnóstico</h3>
              <p>Si tienes dolor, brackets despegados, sensibilidad o inflamación, agenda cita antes de automedicarte.</p>
              <a href={getWhatsappUrl(giganteraBranch, `Hola DentalPuente, leí la guía "${article.title}" y quiero agendar una valoración.`)} target="_blank" rel="noreferrer" className="education-primary-link">Enviar WhatsApp</a>
            </div>
            <div className="education-aside-card education-aside-soft">
              <h3>Relacionado</h3>
              {relatedArticles.map((related) => (
                <Link key={related.slug} to={`${educationBasePath}/${related.slug}`}>{related.title}</Link>
              ))}
            </div>
          </aside>
        </div>
      </article>

      <EducationCta compact />
    </div>
  );
};

const DentalEducation: React.FC = () => {
  const { articleSlug } = useParams<{ articleSlug?: string }>();
  if (!articleSlug) return <EducationLanding />;
  const article = getEducationArticleBySlug(articleSlug);
  if (!article) return <Navigate to={educationBasePath} replace />;
  return <EducationArticlePage article={article} />;
};

export default DentalEducation;
